import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';
import * as tracking from 'dibs-tracking';
import { HomePageContext } from './HomePageContext';
import { ShopLocalNotificationLoader } from '../notifications/shopLocal/ShopLocalNotificationLoader';
import { connectContext } from '../context/helpers/connectContext';
import { getItemsPerPage } from './helpers/getItemsPerPage';
import getLazyLoadStartingPoint from './helpers/getLazyLoadStartingPoint';
import { HpRespAppBannerLazy } from './HpRespAppBanner/HpRespAppBannerLazy';
import { LOCATION_ITEM_PAGE_COUNT } from './constants';
import {
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';
import { componentModuleMap } from './componentModuleMap';

const HpRespLayoutComponent = props => {
    const [hasFiredPageViewTracking, setHasFiredPageViewTracking] = useState(false);
    const [userId, setUserId] = useState(null);
    const [userType, setUserType] = useState(null);
    const [isClient, setIsClient] = useState(null);

    const { relay, isLoggedInUser, viewer, isMobile, isTablet, homepageUserGroup } = props;
    const itemsPerPage = getItemsPerPage({ isMobile, isTablet });

    const user = viewer?.user || null;

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: true });
            relay.refetch(
                fragmentVariables => ({
                    ...fragmentVariables,
                    fetchInitialHomepage: false,
                    isClient: true,
                    isMobile,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    userType: homepageUserGroup || userType || '',
                    userCountryCode: getUserSessionCountryCode(),
                    zipCode: getUserZipCode(),
                    localRecentHistoryItemIds,
                    fetchUserRecentHistoryItem:
                        localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
                    ...additionalVariables,
                    locationItemsCount: itemsPerPage * LOCATION_ITEM_PAGE_COUNT,
                }),
                { fetchInitialHomepage: true },
                () => setIsClient(true)
            );
        },
        [userId, userType, relay, isMobile, itemsPerPage, homepageUserGroup]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            setUserId(cookieUserId);
            setUserType(getUserType(document.cookie));
        }
    }, [userId]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        if (!hasFiredPageViewTracking) {
            setHasFiredPageViewTracking(true);
            tracking.trackPageview();
        }
        setUserData();
        addLoginHandlers(data => {
            if (data?.user?.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload(true);
                return;
            }
            setUserData();
        });
    }, [setUserData, hasFiredPageViewTracking, setHasFiredPageViewTracking]);

    const { homepage = {} } = viewer;

    const { personalization = null, modules } = homepage;
    const showLoggedInModules = isLoggedInUser || userId;
    const promoPrimaryModule = modules.find(mod => mod.type === 'promo' && mod.index === 0);
    const promoPrimaryItemsCount = (promoPrimaryModule?.messages?.items || []).length;
    const startLazyLoadAt = getLazyLoadStartingPoint({ isMobile, isTablet });
    const hasLoggedInWithApp = user?.hasLoggedInWithApp;

    const mods = modules.map((mod, index) => {
        let { type } = mod;

        if (type === 'promo') {
            type = mod.index === 0 ? 'promoPrimary' : 'promoSecondary';
        }

        const component = componentModuleMap[type];
        if (component) {
            return component({
                componentModule: mod,
                key: `${type}-${index}`,
                promoPrimaryItemsCount,
                viewer,
                isMobile,
                isClient,
                itemsPerPage,
                showLoggedInModules,
                personalization,
                useLazyLoadImages: index >= startLazyLoadAt,
                moduleIndex: index,
                totalModules: modules.length,
            });
        }
        return null;
    });

    return (
        <Fragment>
            {mods}
            <ShopLocalNotificationLoader environment={relay.environment} />
            {isClient && isMobile && !(isLoggedInUser && hasLoggedInWithApp) && (
                <HpRespAppBannerLazy />
            )}
        </Fragment>
    );
};

HpRespLayoutComponent.propTypes = {
    homepageUserGroup: PropTypes.string,
    isLoggedInUser: PropTypes.bool,
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
};

function mapContextToProps({ isLoggedInUser, isMobile, isTablet }) {
    return {
        isMobile,
        isTablet,
        isLoggedInUser,
    };
}

const HpRespLayoutWithContext = connectContext(
    HomePageContext,
    mapContextToProps
)(HpRespLayoutComponent);

export const HpRespLayout = createRefetchContainer(
    HpRespLayoutWithContext,
    {
        viewer: graphql`
            fragment HpRespLayout_viewer on Viewer {
                ...HpRespPersonalizedCategories_viewer
                homepage(rootId: $homepageId) {
                    modules {
                        ... on SkinnyBannerModule {
                            type
                            ...HpRespSkinnyBannerModule_componentModule
                        }
                        # don't skip this module on refetch, otherwise the dibs-carousel within
                        # won't get any items
                        ... on TopHeroBannerModule {
                            type
                            ...HpRespTopHeroBannerModule_componentModule
                        }
                        ... on HeroModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespHeroModule_componentModule
                        }
                        ... on CollectionsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespCollectionsModule_componentModule
                        }
                        ... on FeaturedModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespFeaturedModule_componentModule
                        }
                        ... on PromoModule {
                            index
                            type
                            messages(userType: $userType) {
                                items {
                                    cmsId
                                }
                            }
                            ...HpRespPromoModulePrimary_componentModule
                            ...HpRespPromoModuleSecondary_componentModule
                        }
                        ... on EditorialModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespEditorialModule_componentModule
                        }
                        ... on ShopByModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespShopByModule_componentModule
                        }
                        ... on LocationsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespLocationsModule_componentModule
                        }
                        ... on ArrivalsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespPersonalizedCategories_componentModule
                        }
                        ... on RecentlyViewedModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespRecentlyViewedItems_componentModule
                        }
                        ... on CollectionSpotlightModule {
                            type
                            ...HpRespCollectionSpotlightModule_componentModule
                        }
                    }
                    personalization(userId: $userId, first: 3, numItems: 3)
                    @include(if: $hasUserId) {
                        ...HpRespPersonalizedCategories_personalization
                    }
                }
                ...HpRespLocationsModule_viewer
                user(userId: $userId) @include(if: $hasUserId) {
                    ...HpRespRecentlyViewedItems_user
                    hasLoggedInWithApp: hasLoggedInWithDeviceType(deviceType: IOS_APP)
                }
            }
        `,
    },
    graphql`
        query HpRespLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $isClient: Boolean!
            $isMobile: Boolean!
            $fetchInitialHomepage: Boolean!
            $zipCode: String
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
            $locationItemsCount: Int
            $userType: String!
        ) {
            viewer {
                ...HpRespLayout_viewer
            }
        }
    `
);
